import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { NOPING_GREEN, SHIELD_CYAN } from '../constants';

const ISP_DATA = [
  { cy: 16, color: '#4FC3F7', label: 'Wi-Fi' },
  { cy: 44, color: '#66BB6A', label: 'Ethernet' },
  { cy: 72, color: '#2ECDFF', label: '4G' },
  { cy: 100, color: '#AB47BC', label: '5G' },
  { cy: 128, color: '#FFA726', label: 'Fiber' },
  { cy: 156, color: '#EC407A', label: 'DSL' },
];

function PacketDot({ path, color, dur, begin, r = 3 }: { path: string; color: string; dur: string; begin: string; r?: number }) {
  return (
    <>
      <circle r={r} fill={color} opacity='0.9'>
        <animateMotion dur={dur} begin={begin} repeatCount='indefinite' path={path} />
      </circle>
      <circle r={r * 2} fill={color} opacity='0.15'>
        <animateMotion dur={dur} begin={begin} repeatCount='indefinite' path={path} />
      </circle>
    </>
  );
}

interface ShieldDiagramProps {
  failedPipe: number | null;
}

export function ShieldDiagram({ failedPipe }: ShieldDiagramProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });

  const hubX = 210;
  const hubY = 86;
  const serverX = 360;

  return (
    <div ref={ref} className='relative h-44 overflow-hidden' style={{ background: `linear-gradient(180deg, ${SHIELD_CYAN}06 0%, transparent 100%)` }}>
      <svg viewBox='0 0 420 180' className='size-full' preserveAspectRatio='xMidYMid meet'>
        <defs>
          <filter id='v5a-sd-glow'>
            <feGaussianBlur stdDeviation='2.5' result='b' />
            <feMerge>
              <feMergeNode in='b' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
          <filter id='v5a-sd-soft'>
            <feGaussianBlur stdDeviation='6' result='b' />
            <feMerge>
              <feMergeNode in='b' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
          <radialGradient id='v5a-sd-hubfill' cx='50%' cy='50%' r='50%'>
            <stop offset='0%' stopColor={SHIELD_CYAN} stopOpacity='0.15' />
            <stop offset='100%' stopColor={SHIELD_CYAN} stopOpacity='0' />
          </radialGradient>
          <linearGradient id='v5a-sd-output' x1='0' y1='0' x2='1' y2='0'>
            <stop offset='0%' stopColor={SHIELD_CYAN} />
            <stop offset='100%' stopColor={NOPING_GREEN} />
          </linearGradient>
        </defs>

        {Array.from({ length: 8 }).map((_r, row) => Array.from({ length: 16 }).map((_c, col) => <circle key={`bg-${row}-${col}`} cx={20 + col * 25} cy={12 + row * 25} r='0.8' fill='#fff' opacity='0.04' />))}

        {ISP_DATA.map((isp, i) => {
          const isFailed = failedPipe === i;
          const cpX = 120;
          const path = `M70,${isp.cy} C${cpX},${isp.cy} ${cpX + 40},${hubY} ${hubX - 32},${hubY}`;
          return <motion.path key={`path-${i}`} d={path} fill='none' stroke={isFailed ? '#EF4444' : isp.color} strokeWidth={isFailed ? 1 : 1.8} strokeOpacity={isFailed ? 0.15 : 0.35} strokeDasharray={isFailed ? '4 4' : 'none'} initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.8, delay: 0.15 + i * 0.08 }} />;
        })}

        {ISP_DATA.map((isp, i) => {
          const isFailed = failedPipe === i;
          return (
            <motion.g key={`isp-${i}`} initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 0.1 + i * 0.06, stiffness: 200 }}>
              {!isFailed && <circle cx={45} cy={isp.cy} r='16' fill='none' stroke={isp.color} strokeWidth='0.5' opacity='0.2' />}
              <circle cx={45} cy={isp.cy} r='11' fill={isFailed ? '#EF444410' : `${isp.color}12`} stroke={isFailed ? '#EF4444' : isp.color} strokeWidth={isFailed ? 1 : 1.5} strokeDasharray={isFailed ? '3 3' : 'none'} />
              {isFailed ? (
                <>
                  <line x1={40} y1={isp.cy - 5} x2={50} y2={isp.cy + 5} stroke='#EF4444' strokeWidth='1.5' strokeLinecap='round' />
                  <line x1={50} y1={isp.cy - 5} x2={40} y2={isp.cy + 5} stroke='#EF4444' strokeWidth='1.5' strokeLinecap='round' />
                </>
              ) : (
                <circle cx={45} cy={isp.cy} r='2' fill={isp.color} />
              )}
              <text x={45} y={isp.cy + 18} textAnchor='middle' fill={isFailed ? '#EF4444' : isp.color} fontSize='6' fontFamily='var(--font-outfit)' fontWeight='600' opacity={isFailed ? 0.5 : 0.7}>
                {isFailed ? 'Failed' : isp.label}
              </text>
            </motion.g>
          );
        })}

        {ISP_DATA.map((isp, i) => {
          if (failedPipe === i) return null;
          const path = `M70,${isp.cy} C120,${isp.cy} 160,${hubY} ${hubX - 32},${hubY}`;
          return (
            <g key={`pkt-${i}`}>
              <PacketDot path={path} color={isp.color} dur={`${1.6 + i * 0.15}s`} begin={`${i * 0.25}s`} r={2.5} />
              <PacketDot path={path} color={isp.color} dur={`${1.6 + i * 0.15}s`} begin={`${i * 0.25 + 0.8}s`} r={2} />
            </g>
          );
        })}

        <motion.g initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 0.5, stiffness: 150 }}>
          <circle cx={hubX} cy={hubY} r='40' fill='url(#v5a-sd-hubfill)' filter='url(#v5a-sd-soft)' />
          <motion.circle cx={hubX} cy={hubY} r='36' fill='none' stroke={SHIELD_CYAN} strokeWidth='1' animate={{ r: [36, 42, 36], opacity: [0.3, 0.08, 0.3] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
          <circle cx={hubX} cy={hubY} r='30' fill='#0a1a1f' stroke={SHIELD_CYAN} strokeWidth='2' filter='url(#v5a-sd-glow)' />
          <g transform={`translate(${hubX - 12}, ${hubY - 16})`}>
            <path d='M12 2L3 7v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7L12 2z' fill={`${SHIELD_CYAN}25`} stroke={SHIELD_CYAN} strokeWidth='1.5' strokeLinejoin='round' />
            <motion.path d='M9 13l2.5 2.5 4.5-4.5' fill='none' stroke={SHIELD_CYAN} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }} />
          </g>
          <text x={hubX} y={hubY + 40} textAnchor='middle' fill={SHIELD_CYAN} fontSize='7' fontWeight='700' fontFamily='var(--font-bai)'>
            NoPing Shield
          </text>
        </motion.g>

        <motion.path d={`M${hubX + 32},${hubY} C${hubX + 60},${hubY} ${serverX - 50},${hubY} ${serverX - 20},${hubY}`} fill='none' stroke='url(#v5a-sd-output)' strokeWidth='2.5' strokeOpacity='0.5' initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : {}} transition={{ duration: 0.6, delay: 0.7 }} />
        <PacketDot path={`M${hubX + 32},${hubY} L${serverX - 20},${hubY}`} color={SHIELD_CYAN} dur='1s' begin='0s' r={3} />
        <PacketDot path={`M${hubX + 32},${hubY} L${serverX - 20},${hubY}`} color={NOPING_GREEN} dur='1s' begin='0.5s' r={2.5} />

        <motion.g initial={{ scale: 0, opacity: 0 }} animate={inView ? { scale: 1, opacity: 1 } : {}} transition={{ type: 'spring', delay: 0.8 }}>
          <circle cx={serverX} cy={hubY} r='20' fill='#0a1a0f' stroke={NOPING_GREEN} strokeWidth='1.5' />
          <rect x={serverX - 8} y={hubY - 10} width='16' height='6' rx='1.5' fill='none' stroke={NOPING_GREEN} strokeWidth='1' />
          <rect x={serverX - 8} y={hubY - 2} width='16' height='6' rx='1.5' fill='none' stroke={NOPING_GREEN} strokeWidth='1' />
          <motion.circle cx={serverX + 5} cy={hubY - 7} r='1.2' fill={NOPING_GREEN} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity }} />
          <circle cx={serverX + 5} cy={hubY + 1} r='1.2' fill={NOPING_GREEN} opacity='0.6' />
          <text x={serverX} y={hubY + 30} textAnchor='middle' fill={NOPING_GREEN} fontSize='7' fontFamily='var(--font-outfit)' fontWeight='600' opacity='0.8'>
            Game Server
          </text>
        </motion.g>

        <motion.g initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 1 }}>
          <rect x={hubX - 48} y={hubY + 48} width={96} height={18} rx={9} fill={`${SHIELD_CYAN}12`} stroke={SHIELD_CYAN} strokeWidth='0.8' />
          <text x={hubX} y={hubY + 60} textAnchor='middle' fill={SHIELD_CYAN} fontSize='6.5' fontWeight='700' fontFamily='var(--font-outfit)'>
            <animate attributeName='opacity' values='0.6;1;0.6' dur='2s' repeatCount='indefinite' />
            0% DOWNTIME
          </text>
        </motion.g>
      </svg>
    </div>
  );
}
